import { LogOut, Smartphone } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/shared/ui/empty'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import type { ClientAccountDeviceV2 } from '../services/client-accounts-admin-v2.service'

/**
 * Los teléfonos con sesión VIVA. No es un historial: `auth_sessions` tiene TTL y sus
 * filas se borran al vencer, así que esto es «quién está conectado ahora».
 *
 * Cerrar una sesión NO es bloquear. Bloquear además impide volver a entrar hasta que
 * alguien reactive la cuenta; esto deja al cliente entrar de nuevo con un código. Es la
 * diferencia que hace útil el botón para el caso del teléfono perdido.
 */
type ClientAccountDevicesListProps = {
  devices: ClientAccountDeviceV2[]
  canManage: boolean
  onRevoke: (sessionId: string) => void
  isRevoking: boolean
}

export function ClientAccountDevicesList({
  devices,
  canManage,
  onRevoke,
  isRevoking
}: ClientAccountDevicesListProps) {
  if (devices.length === 0) {
    return (
      <Empty className="py-6">
        <EmptyHeader>
          <EmptyMedia>
            <Smartphone className="h-6 w-6 text-muted-foreground" aria-hidden />
          </EmptyMedia>
          <EmptyTitle>Ningún dispositivo conectado</EmptyTitle>
          <EmptyDescription>
            Aparecerán aquí cuando el cliente entre desde su teléfono. Las sesiones vencen solas,
            así que esta lista no es un historial.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <ul className="flex flex-col gap-1.5">
      {devices.map((device) => (
        <li
          key={device.id}
          className="flex items-start gap-2 rounded-md border border-border px-2.5 py-2"
        >
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <Smartphone className="h-3.5 w-3.5" aria-hidden />
          </span>

          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            {/* La app manda el nombre del equipo al iniciar sesión, pero no siempre: las
                sesiones viejas no lo tienen. Se dice «Dispositivo sin identificar» en vez
                de dejar el renglón en blanco, que se lee como un error de carga. */}
            <span className="truncate text-xs font-medium">
              {device.deviceName || 'Dispositivo sin identificar'}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {device.platform === 'ios'
                ? 'iPhone'
                : device.platform === 'android'
                  ? 'Android'
                  : 'Plataforma desconocida'}
              {device.ip ? ` · ${device.ip}` : ''}
            </span>
            <span className="text-xs text-muted-foreground">
              {device.lastUsedAt
                ? `Última actividad ${formatHumanDateTime(device.lastUsedAt)}`
                : `Ingresó ${formatHumanDateTime(device.createdAt)}`}
            </span>
          </div>

          {canManage ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
              disabled={isRevoking}
              onClick={() => {
                onRevoke(device.id)
              }}
              aria-label={`Cerrar la sesión de ${device.deviceName || 'este dispositivo'}`}
            >
              <LogOut className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  )
}
